import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FaClipboardCheck, FaSpinner, FaChevronDown, FaChevronUp, FaPills, FaHeartbeat, FaExclamationTriangle } from 'react-icons/fa';
import { dashboardAPI } from '../../services/dashboardAPI';
import { toast } from 'react-toastify';

// Common side effects for GLP-1 medications
const commonSideEffects = [
  { id: 'nausea', label: 'Nausea' },
  { id: 'vomiting', label: 'Vomiting' },
  { id: 'diarrhea', label: 'Diarrhea' },
  { id: 'constipation', label: 'Constipation' },
  { id: 'headache', label: 'Headache' },
  { id: 'fatigue', label: 'Fatigue' },
  { id: 'dizziness', label: 'Dizziness' },
  { id: 'injection_site', label: 'Injection Site Reaction' },
  { id: 'appetite_loss', label: 'Loss of Appetite' },
  { id: 'abdominal_pain', label: 'Abdominal Pain' },
];

const validationSchema = Yup.object({
  weight: Yup.number()
    .required('Weight is required')
    .min(20, 'Weight must be at least 20 kg')
    .max(300, 'Weight must be less than 300 kg'),
  waistMeasurement: Yup.number()
    .min(40, 'Must be at least 40 cm')
    .max(200, 'Must be less than 200 cm')
    .nullable(),
  hipMeasurement: Yup.number()
    .min(40, 'Must be at least 40 cm')
    .max(200, 'Must be less than 200 cm')
    .nullable(),
  chestMeasurement: Yup.number()
    .min(40, 'Must be at least 40 cm')
    .max(200, 'Must be less than 200 cm')
    .nullable(),
  sleepHours: Yup.number()
    .min(0, 'Cannot be negative')
    .max(24, 'Cannot exceed 24 hours')
    .nullable(),
  waterIntake: Yup.number()
    .min(0, 'Cannot be negative')
    .max(20, 'Seems too high')
    .nullable(),
  exerciseDone: Yup.string().max(1000, 'Too long'),
  challengesFaced: Yup.string().max(1000, 'Too long'),
  goalsNextWeek: Yup.string().max(1000, 'Too long'),
  overallMood: Yup.number().min(1).max(5).nullable(),
  additionalNotes: Yup.string().max(2000, 'Too long'),
  // Medical fields validation
  medicationTaken: Yup.boolean().nullable(),
  missedDoses: Yup.number().min(0).max(7).nullable(),
  currentDosage: Yup.string().max(100).nullable(),
  injectionSite: Yup.string().max(100).nullable(),
  bloodPressureSystolic: Yup.number().min(60).max(250).nullable(),
  bloodPressureDiastolic: Yup.number().min(40).max(150).nullable(),
  bloodSugarFasting: Yup.number().min(20).max(600).nullable(),
  bloodSugarPostMeal: Yup.number().min(20).max(600).nullable(),
  heartRate: Yup.number().min(30).max(220).nullable(),
  sideEffects: Yup.string().max(500).nullable(),
  sideEffectsSeverity: Yup.number().min(1).max(5).nullable(),
  sideEffectsNotes: Yup.string().max(1000).nullable(),
  appetiteLevel: Yup.number().min(1).max(5).nullable(),
  energyLevel: Yup.number().min(1).max(5).nullable(),
  medicalConcerns: Yup.string().max(2000).nullable(),
});

const initialValues = {
  weight: '',
  waistMeasurement: '',
  hipMeasurement: '',
  chestMeasurement: '',
  sleepHours: '',
  waterIntake: '',
  exerciseDone: '',
  challengesFaced: '',
  goalsNextWeek: '',
  overallMood: 3,
  additionalNotes: '',
  // Medical fields
  medicationTaken: null,
  missedDoses: '',
  currentDosage: '',
  injectionSite: '',
  bloodPressureSystolic: '',
  bloodPressureDiastolic: '',
  bloodSugarFasting: '',
  bloodSugarPostMeal: '',
  heartRate: '',
  sideEffects: '',
  sideEffectsSeverity: null,
  sideEffectsNotes: '',
  appetiteLevel: 3,
  energyLevel: 3,
  medicalConcerns: '',
};

const moodEmojis = [
  { value: 1, emoji: '😞', label: 'Very Low' },
  { value: 2, emoji: '😕', label: 'Low' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '🙂', label: 'Good' },
  { value: 5, emoji: '😄', label: 'Great' },
];

const severityLabels = [
  { value: 1, label: 'Mild', color: 'bg-green-100 text-green-800' },
  { value: 2, label: 'Light', color: 'bg-lime-100 text-lime-800' },
  { value: 3, label: 'Moderate', color: 'bg-yellow-100 text-yellow-800' },
  { value: 4, label: 'Strong', color: 'bg-orange-100 text-orange-800' },
  { value: 5, label: 'Severe', color: 'bg-red-100 text-red-800' },
];

const levelLabels = [
  { value: 1, label: 'Very Low' },
  { value: 2, label: 'Low' },
  { value: 3, label: 'Normal' },
  { value: 4, label: 'Good' },
  { value: 5, label: 'Excellent' },
];

const WeeklyCheckInForm = ({ currentWeek, onSuccess, existingData, planType = 'CORE' }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedSideEffects, setSelectedSideEffects] = useState([]);

  const isMedicalPlan = planType === 'MEDICAL' || planType === 'GLP1';
  const isGLP1Plan = planType === 'GLP1';

  // Initialize side effects from existing data
  React.useEffect(() => {
    if (existingData?.sideEffects) {
      setSelectedSideEffects(existingData.sideEffects.split(',').filter(Boolean));
    }
  }, [existingData]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const clean = (v) => (v === '' || v == null) ? null : v;
      const toFloat = (v) => (v === '' || v == null) ? null : parseFloat(v);
      const toInt = (v) => (v === '' || v == null) ? null : parseInt(v);

      // Map camelCase Formik values → snake_case DB columns
      const payload = {
        week_number: currentWeek,
        weight: toFloat(values.weight),
        waist_measurement: toFloat(values.waistMeasurement),
        hip_measurement: toFloat(values.hipMeasurement),
        chest_measurement: toFloat(values.chestMeasurement),
        sleep_hours: toFloat(values.sleepHours),
        water_intake: toFloat(values.waterIntake),
        exercise_done: clean(values.exerciseDone),
        challenges_faced: clean(values.challengesFaced),
        goals_next_week: clean(values.goalsNextWeek),
        overall_mood: clean(values.overallMood),
        additional_notes: clean(values.additionalNotes),
        medication_taken: values.medicationTaken,
        missed_doses: toInt(values.missedDoses),
        current_dosage: clean(values.currentDosage),
        injection_site: clean(values.injectionSite),
        blood_pressure_systolic: toInt(values.bloodPressureSystolic),
        blood_pressure_diastolic: toInt(values.bloodPressureDiastolic),
        blood_sugar_fasting: toFloat(values.bloodSugarFasting),
        blood_sugar_post_meal: toFloat(values.bloodSugarPostMeal),
        heart_rate: toInt(values.heartRate),
        side_effects: clean(values.sideEffects),
        side_effects_severity: clean(values.sideEffectsSeverity),
        side_effects_notes: clean(values.sideEffectsNotes),
        appetite_level: clean(values.appetiteLevel),
        energy_level: clean(values.energyLevel),
        medical_concerns: clean(values.medicalConcerns),
      };

      await dashboardAPI.submitCheckIn(payload);
      toast.success('Check-in submitted successfully!');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Check-in failed:', error);
      toast.error(error.message || 'Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
            <FaClipboardCheck className="text-xl text-primary-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-bold text-gray-900">Weekly Check-in</h3>
            <p className="text-sm text-gray-500">Week {currentWeek}</p>
          </div>
        </div>
        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {/* Form */}
      {isExpanded && (
        <Formik
          initialValues={existingData || initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className="p-6 pt-0 space-y-6">
              {/* Measurements Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Body Measurements</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weight (kg) *
                    </label>
                    <Field
                      name="weight"
                      type="number"
                      step="0.1"
                      placeholder="65.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <ErrorMessage name="weight" component="p" className="text-red-500 text-xs mt-1" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Waist (cm)
                    </label>
                    <Field
                      name="waistMeasurement"
                      type="number"
                      step="0.1"
                      placeholder="80"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hip (cm)
                    </label>
                    <Field
                      name="hipMeasurement"
                      type="number"
                      step="0.1"
                      placeholder="95"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chest (cm)
                    </label>
                    <Field
                      name="chestMeasurement"
                      type="number"
                      step="0.1"
                      placeholder="90"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Lifestyle Section */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Lifestyle</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sleep (hours)
                    </label>
                    <Field
                      name="sleepHours"
                      type="number"
                      step="0.5"
                      placeholder="7.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Water (liters)
                    </label>
                    <Field
                      name="waterIntake"
                      type="number"
                      step="0.1"
                      placeholder="2.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Exercise */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exercise Done This Week
                </label>
                <Field
                  as="textarea"
                  name="exerciseDone"
                  rows={2}
                  placeholder="e.g., 3 days gym, 2 days walking, yoga on weekends..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Challenges & Goals */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Challenges Faced
                  </label>
                  <Field
                    as="textarea"
                    name="challengesFaced"
                    rows={3}
                    placeholder="Any difficulties this week..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Goals for Next Week
                  </label>
                  <Field
                    as="textarea"
                    name="goalsNextWeek"
                    rows={3}
                    placeholder="What you want to achieve..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>

              {/* Mood */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overall Mood This Week
                </label>
                <div className="flex justify-between gap-2">
                  {moodEmojis.map((mood) => (
                    <button
                      key={mood.value}
                      type="button"
                      onClick={() => setFieldValue('overallMood', mood.value)}
                      className={`flex-1 flex flex-col items-center gap-1 py-3 rounded-lg transition-colors ${
                        values.overallMood === mood.value
                          ? 'bg-primary-100 ring-2 ring-primary-500'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <span className="text-2xl">{mood.emoji}</span>
                      <span className="text-xs text-gray-600">{mood.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <Field
                  as="textarea"
                  name="additionalNotes"
                  rows={2}
                  placeholder="Anything else you'd like to share..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* ===== MEDICAL SECTION (for MEDICAL/GLP1 plans) ===== */}
              {isMedicalPlan && (
                <div className="border-t-2 border-purple-200 pt-6 mt-6">
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <FaPills className="text-xl text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">Medical Check-in</h4>
                      <p className="text-sm text-gray-500">
                        {isGLP1Plan ? 'GLP-1 medication tracking' : 'Medical program tracking'}
                      </p>
                    </div>
                  </div>

                  {/* Medication Compliance */}
                  <div className="bg-purple-50 rounded-xl p-4 mb-6">
                    <h5 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                      <FaPills className="text-purple-600" />
                      Medication Compliance
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Did you take your medication this week?
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setFieldValue('medicationTaken', true)}
                            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                              values.medicationTaken === true
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setFieldValue('medicationTaken', false)}
                            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                              values.medicationTaken === false
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Missed Doses
                        </label>
                        <Field
                          name="missedDoses"
                          type="number"
                          min="0"
                          max="7"
                          placeholder="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Current Dosage
                        </label>
                        <Field
                          name="currentDosage"
                          type="text"
                          placeholder="e.g., 0.5mg weekly"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      {isGLP1Plan && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Injection Site
                          </label>
                          <Field
                            as="select"
                            name="injectionSite"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="">Select site</option>
                            <option value="abdomen">Abdomen</option>
                            <option value="thigh">Thigh</option>
                            <option value="upper_arm">Upper Arm</option>
                          </Field>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Vital Signs */}
                  <div className="bg-blue-50 rounded-xl p-4 mb-6">
                    <h5 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <FaHeartbeat className="text-blue-600" />
                      Vital Signs (Optional)
                    </h5>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          BP Systolic
                        </label>
                        <Field
                          name="bloodPressureSystolic"
                          type="number"
                          placeholder="120"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          BP Diastolic
                        </label>
                        <Field
                          name="bloodPressureDiastolic"
                          type="number"
                          placeholder="80"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Heart Rate
                        </label>
                        <Field
                          name="heartRate"
                          type="number"
                          placeholder="bpm"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fasting Blood Sugar
                        </label>
                        <Field
                          name="bloodSugarFasting"
                          type="number"
                          placeholder="mg/dL"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Post-Meal Blood Sugar
                        </label>
                        <Field
                          name="bloodSugarPostMeal"
                          type="number"
                          placeholder="mg/dL"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Side Effects */}
                  <div className="bg-amber-50 rounded-xl p-4 mb-6">
                    <h5 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                      <FaExclamationTriangle className="text-amber-600" />
                      Side Effects
                    </h5>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select any side effects experienced:
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {commonSideEffects.map((effect) => {
                          const isSelected = selectedSideEffects.includes(effect.id);
                          return (
                            <button
                              key={effect.id}
                              type="button"
                              onClick={() => {
                                let newEffects;
                                if (isSelected) {
                                  newEffects = selectedSideEffects.filter(e => e !== effect.id);
                                } else {
                                  newEffects = [...selectedSideEffects, effect.id];
                                }
                                setSelectedSideEffects(newEffects);
                                setFieldValue('sideEffects', newEffects.join(','));
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                isSelected
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                              }`}
                            >
                              {effect.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {selectedSideEffects.length > 0 && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Severity Level
                          </label>
                          <div className="flex gap-1">
                            {severityLabels.map((level) => (
                              <button
                                key={level.value}
                                type="button"
                                onClick={() => setFieldValue('sideEffectsSeverity', level.value)}
                                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                                  values.sideEffectsSeverity === level.value
                                    ? level.color + ' ring-2 ring-offset-1 ring-amber-400'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {level.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Side Effects Notes
                          </label>
                          <Field
                            as="textarea"
                            name="sideEffectsNotes"
                            rows={2}
                            placeholder="Describe when and how severe..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Energy & Appetite */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Appetite Level
                      </label>
                      <div className="flex gap-1">
                        {levelLabels.map((level) => (
                          <button
                            key={level.value}
                            type="button"
                            onClick={() => setFieldValue('appetiteLevel', level.value)}
                            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                              values.appetiteLevel === level.value
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {level.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Energy Level
                      </label>
                      <div className="flex gap-1">
                        {levelLabels.map((level) => (
                          <button
                            key={level.value}
                            type="button"
                            onClick={() => setFieldValue('energyLevel', level.value)}
                            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                              values.energyLevel === level.value
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {level.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Medical Concerns */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Medical Concerns for Doctor
                    </label>
                    <Field
                      as="textarea"
                      name="medicalConcerns"
                      rows={3}
                      placeholder="Any symptoms, concerns, or questions for your doctor..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This will be reviewed by your assigned doctor
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FaClipboardCheck />
                    Submit Check-in
                  </>
                )}
              </button>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default WeeklyCheckInForm;
